import React, { useState } from 'react';
import { Task, TaskStatus } from '../../types/task.types';
import { TaskGroup, TaskGroupKey } from '../../hooks/useTaskQueries';
import { PRIORITY_BG, dueStamp, isTaskOverdue } from './taskMeta';
import { LedgerSkeleton } from '../shell/LedgerSkeleton';
import { cn } from '../../lib/cn';

const COLUMN_STATUS: Partial<Record<TaskGroupKey, TaskStatus>> = {
  pending: 'TODO',
  'in-progress': 'IN_PROGRESS',
  'awaiting-approval': 'REVIEW',
  completed: 'COMPLETED',
};

export interface TaskBoardProps {
  columns: TaskGroup[];
  selectedId: string | null;
  busy: boolean;
  onSelect: (id: string) => void;
  onDropStatus: (task: Task, status: TaskStatus) => void;
}

export const TaskBoard: React.FC<TaskBoardProps> = (props) => {
  const [dragged, setDragged] = useState<Task | null>(null);

  if (props.columns.every((column) => column.isPending)) {
    return <LedgerSkeleton lines={8} className="mt-4" />;
  }

  // Column membership follows the task's current status, not which query the
  // row came from: an optimistic status change must move the card immediately
  // instead of waiting for the per-column caches to refetch.
  const pooled = new Map(props.columns.flatMap((column) => column.rows).map((task) => [task.id, task]));
  const rowsFor = (status: TaskStatus) =>
    [...pooled.values()].filter((task) => task.status === status);

  // Track count follows the number of visible columns so a filtered single
  // column (e.g. the awaiting-approval view) fills the width instead of sitting
  // in one of four fixed tracks.
  const renderColumns = props.columns.filter((column) => COLUMN_STATUS[column.key]);

  return (
    // Columns hold a floor of 15rem and the board scrolls sideways below
    // that, rather than sharing the width down to a few crushed characters.
    // Four columns used to start clipping card titles around 1355px and by
    // 1000px were losing the risk reference as well.
    <div className="-mx-1 mt-3 overflow-x-auto px-1">
      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: `repeat(${Math.max(1, renderColumns.length)}, minmax(15rem, 1fr))`,
        }}
      >
      {renderColumns.map((column) => {
        const status = COLUMN_STATUS[column.key]!;
        const rows = rowsFor(status);
        return (
          <section
            key={column.key}
            aria-label={column.label}
            className="min-w-0"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              if (dragged && dragged.status !== status) props.onDropStatus(dragged, status);
              setDragged(null);
            }}
          >
            <div className="cap-label border-b border-ink pb-0.5">
              {column.label} · {column.count}
            </div>
            <div className="space-y-2 pt-2">
              {rows.map((task) => (
                <div
                  key={task.id}
                  data-testid="board-card"
                  draggable={!props.busy}
                  onDragStart={(event) => {
                    setDragged(task);
                    // Required by Firefox for a native drag to start at all.
                    event.dataTransfer.setData('text/plain', task.id);
                  }}
                  onDragEnd={() => setDragged(null)}
                  onClick={() => props.onSelect(task.id)}
                  aria-current={task.id === props.selectedId ? 'true' : undefined}
                  className={cn(
                    'cursor-pointer border border-rule-faint bg-paper-raised p-2 hover:bg-accent-soft',
                    task.id === props.selectedId && 'shadow-[inset_2px_0_0_var(--color-accent)]',
                    props.busy && 'opacity-60',
                  )}
                >
                  <div className="flex items-baseline gap-1.5 text-scale-1 text-ink">
                    <span aria-hidden className={cn('h-2 w-2 shrink-0 self-center', PRIORITY_BG[task.priority])} />
                    {/* Two lines, not one with an ellipsis. A card whose title
                        is cut off is a card you have to open to identify. */}
                    <span className="line-clamp-2 min-w-0 flex-1 font-medium">{task.title}</span>
                  </div>
                  {/* References get a line to themselves. Sharing one with the
                      due date left both clipped in a 15rem column, and a
                      half-printed reference identifies nothing. */}
                  <div className="mt-1 truncate font-mono text-scale-0 text-ink-muted">
                    {task.reference_number}
                    {task.riskReference ? ` ↳ ${task.riskReference}` : ''}
                  </div>
                  <div className="mt-0.5 flex items-baseline justify-between gap-2 text-scale-0 text-ink-muted">
                    <span className="min-w-0 truncate">{task.assigneeName ?? 'Unassigned'}</span>
                    <span
                      className={cn('shrink-0 font-mono', isTaskOverdue(task) && 'text-sev-critical')}
                    >
                      {dueStamp(task) ?? '—'}
                    </span>
                  </div>
                </div>
              ))}
              {column.hasMore && (
                <button
                  type="button"
                  onClick={column.fetchMore}
                  disabled={column.isFetchingMore}
                  className="cursor-pointer py-1 font-serif text-scale-1 italic text-ink-muted underline hover:text-accent disabled:opacity-40"
                >
                  Show {Math.max(0, column.count - column.rows.length)} more
                </button>
              )}
            </div>
          </section>
        );
      })}
      </div>
    </div>
  );
};
